// messageCenter/messageCenterApi.js — transport of the message center (ajax.php cases messagecenter_*, CSRF header like
// the other vite modules). Every answer is { success, data } or { success: false, error, message? } (MessageCenterApi.php,
// NoticeApi.php).
// With files the request goes as multipart (files[] + roles[], MessageCenterUpload::fromPhpFiles), else form-encoded.

const AJAX_URL = '/includes/ajax.php';

function csrfHeaders() {
    const tok = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
    return tok ? { Csrftoken: tok } : {};
}

/** Adds the params to a URLSearchParams or FormData; arrays as key[] (PHP reads arrays), null/undefined left out. */
function appendParams(target, params) {
    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined) { continue; }
        if (Array.isArray(value)) { value.forEach((v) => target.append(key + '[]', v)); }
        else { target.append(key, value); }
    }
    return target;
}

/** Form body for a request without files. */
export function toBody(params) {
    return appendParams(new URLSearchParams(), params);
}

/** Multipart body: the params plus files[] with the parallel roles[]. */
export function toFormData(params, files) {
    const body = appendParams(new FormData(), params);
    for (const { file, role } of files) {
        body.append('files[]', file, file.name);
        body.append('roles[]', role);
    }
    return body;
}

async function post(action, params = {}, files = []) {
    const all = { action: 'messagecenter_' + action, ...params };
    const res = await fetch(AJAX_URL, {
        method: 'POST',
        credentials: 'same-origin',
        // multipart: no Content-Type, the browser sets it with the boundary
        headers: files.length ? csrfHeaders() : { 'Content-Type': 'application/x-www-form-urlencoded', ...csrfHeaders() },
        body: files.length ? toFormData(all, files) : toBody(all),
    });
    if (!res.ok) { throw new Error('HTTP ' + res.status); }
    return res.json();
}

export function createMessageCenterApi() {
    return {
        listTickets:   (filter = {}) => post('ticket_list', filter),
        // markRead false: the ZIP export reads a ticket without marking it read (D-46)
        getTicket:     (ticketId, { markRead = true } = {}) => post('ticket_get', { ticket_id: ticketId, mark_read: markRead ? null : 0 }),
        createTicket:  (ticket, files = []) => post('ticket_create', { ...ticket, context: ticket.context ? JSON.stringify(ticket.context) : null }, files),
        commentTicket: (ticketId, body, files = []) => post('ticket_comment', { ticket_id: ticketId, body }, files),
        changeTicket:  (ticketId, field, value) => post('ticket_change', { ticket_id: ticketId, field, value: value ?? '' }),
        listAssignees: () => post('assignees'),
        listAreas:     () => post('ticket_areas'),
        // the ticket import (phase 13): a preview, unless apply; without dry_run the server only checks
        importTickets: (text, { apply = false } = {}) => post('ticket_import', { text, dry_run: apply ? 0 : null }),
        // notices (NoticeApi.php). null is left out of a request, so a cleared expiry has to be sent as ''.
        listNotices:       (params = {}) => post('notice_list', params),
        getNotice:         (threadId) => post('notice_get', { thread_id: threadId }),
        pendingNotices:    () => post('notice_pending'),
        acknowledgeNotice: (threadId) => post('notice_acknowledge', { thread_id: threadId }),
        createNotice:      (notice) => post('notice_create', notice),
        updateNotice:      (threadId, changes) => post('notice_update', { thread_id: threadId, ...changes }),
        reshowNotice:      (threadId) => post('notice_reshow', { thread_id: threadId }),
        // the inbox button on every backend page (InboxSummaryApi.php): counter + notices to pop up
        summary:           () => post('summary'),
        // an attachment from messagecenter_file.php for a ZIP (D-46): bytes, or null on 404 (missing or not allowed,
        // the endpoint does not tell them apart); any other failure throws
        getFile: async (url) => {
            const res = await fetch(url, { credentials: 'same-origin' });
            if (res.status === 404) { return null; }
            if (!res.ok) { throw new Error('HTTP ' + res.status + ' for ' + url); }
            return new Uint8Array(await res.arrayBuffer());
        },
    };
}
